import { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw } from "lucide-react";
import { useSessionTimer, useStartTimer, usePauseTimer, useResetTimer, useSessionToolsStream } from "@/lib/sessionToolsQueries";

/**
 * A manual, coach-controlled stopwatch — added to a Session's Prep plan
 * as resource_type="tool" (resource_id "timer"). The coach starts/
 * pauses/resets; the client only ever observes it, live. Deliberately
 * not an auto-countdown to the session's scheduled end — this is a
 * plain stopwatch, timing whatever the coach chooses to time.
 */
function formatElapsed(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export default function SessionTimerTool({ workspaceId, sesId, isCoach }) {
  useSessionToolsStream({ workspaceId, sesId, enabled: true });
  const { data } = useSessionTimer(workspaceId, sesId);
  const start = useStartTimer(workspaceId, sesId);
  const pause = usePauseTimer(workspaceId, sesId);
  const reset = useResetTimer(workspaceId, sesId);
  const [displayMs, setDisplayMs] = useState(0);
  // The server sends the true elapsed time at the moment it responds
  // (never a running "since" timestamp for the client to trust
  // indefinitely) — anchor a local 500ms tick off of that snapshot so
  // the display stays smooth between syncs without drifting.
  const syncRef = useRef({ elapsedMs: 0, syncedAt: Date.now(), running: false });

  useEffect(() => {
    if (!data) return;
    syncRef.current = { elapsedMs: data.elapsed_ms, syncedAt: Date.now(), running: data.status === "running" };
    setDisplayMs(data.elapsed_ms);
  }, [data]);

  useEffect(() => {
    if (data?.status !== "running") return undefined;
    const interval = setInterval(() => {
      const { elapsedMs, syncedAt } = syncRef.current;
      setDisplayMs(elapsedMs + (Date.now() - syncedAt));
    }, 500);
    return () => clearInterval(interval);
  }, [data]);

  const status = data?.status || "idle";

  return (
    <div className="flex flex-col items-center gap-4 py-8" data-testid="session-timer-tool">
      <p className="text-5xl font-mono tabular-nums text-ink" data-testid="session-timer-display">{formatElapsed(displayMs)}</p>
      <p className="text-xs text-ink/50 uppercase tracking-wide" data-testid="session-timer-status">
        {status === "running" ? "Running" : status === "paused" ? "Paused" : "Not started"}
      </p>
      {isCoach ? (
        <div className="flex items-center gap-2">
          {status === "running" ? (
            <button onClick={() => pause.mutate()} className="flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full bg-ink text-paper hover:bg-ink/85 transition-colors" data-testid="session-timer-pause-button">
              <Pause size={14} /> Pause
            </button>
          ) : (
            <button onClick={() => start.mutate()} className="flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full bg-terracotta text-paper hover:bg-terracotta/90 transition-colors" data-testid="session-timer-start-button">
              <Play size={14} /> {status === "paused" ? "Resume" : "Start"}
            </button>
          )}
          <button onClick={() => reset.mutate()} className="flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full border border-ink/20 hover:border-ink/40 transition-colors" data-testid="session-timer-reset-button">
            <RotateCcw size={14} /> Reset
          </button>
        </div>
      ) : (
        <p className="text-xs text-ink/40" data-testid="session-timer-client-hint">Your coach controls this timer</p>
      )}
    </div>
  );
}
